import { mapGetters, mapMutations } from 'vuex';
import Vue from 'vue';
import store from '../../../../store';
import productSearchService from '../../../../services/product/search';

export default {
  components: {
    VCard: require('../../../../components/card/card.vue'),
    VNoData: require('../../../../components/noData/noData.vue'),
    VPagination: require('../../../../components/paginationNew/paginationNew.vue'),
    VText: require('../../../../components/form/text/text.vue'),
    VSelect: require('../../../../components/form/select/select.vue'),
  },

  data() {
    return {
      loading: true,
      items: [],
      clients: [],
      showImportDialog: false,
      importing: false,
      importForm: {
        client_id: null,
        name: '',
      },
      importFile: null,
      /** After import: { agreement, lines_created, lines_skipped, unmatched }. Unmatched table only shown when unmatched.length > 0. */
      importResult: null,
      /** Per-row state for unmatched table: { [idx]: { productId, productOptions, loading } } */
      unmatchedSelections: {},
      currentPage: 1,
      perPage: 25,
      unmatchedPerPage: 20,
      currentUnmatchedPage: 1,
      searchQuery: '',
      /** 'all' | 'active' | 'inactive' - default inactive */
      activeFilter: 'inactive',
      deletingId: null,
      /** Card vs table: same as clients - info/primary toggled by changeView */
      info: 'info',
      primary: 'primary',
      sort: 'name|asc',
      sortTypeIds: [
        'name|asc',
        'name|desc',
        'client|asc',
        'client|desc',
        'validFrom|desc',
        'validFrom|asc',
      ],
    };
  },

  computed: {
    ...mapGetters({
      view: 'getView',
    }),
    filteredItems() {
      let list = this.items;
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (q) {
        list = list.filter((a) => {
          const name = (a.name || '').toLowerCase();
          const clientName = (a.client && a.client.name) ? a.client.name.toLowerCase() : '';
          return name.indexOf(q) !== -1 || clientName.indexOf(q) !== -1;
        });
      }
      if (this.activeFilter === 'active') {
        list = list.filter((a) => a.is_active === true);
      } else if (this.activeFilter === 'inactive') {
        list = list.filter((a) => a.is_active !== true);
      }
      return list;
    },
    sortedFilteredItems() {
      const list = [...this.filteredItems];
      const [field, dir] = (this.sort || 'name|asc').split('|');
      const mult = dir === 'desc' ? -1 : 1;
      list.sort((a, b) => {
        let va; let vb;
        if (field === 'name') {
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
        } else if (field === 'client') {
          va = (a.client && a.client.name ? a.client.name : '').toLowerCase();
          vb = (b.client && b.client.name ? b.client.name : '').toLowerCase();
        } else if (field === 'validFrom') {
          va = a.valid_from || '';
          vb = b.valid_from || '';
        } else {
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
        }
        if (va < vb) return -1 * mult;
        if (va > vb) return 1 * mult;
        return 0;
      });
      return list;
    },
    pagination() {
      const total = this.sortedFilteredItems.length;
      const limit = this.perPage;
      return {
        currentPage: this.currentPage,
        lastPage: Math.max(1, Math.ceil(total / limit)),
        limit,
        totalCount: total,
      };
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.sortedFilteredItems.slice(start, start + this.perPage);
    },
    sortTypes() {
      const keys = {
        'name|asc': 'pricingAgreements.sortNameAsc',
        'name|desc': 'pricingAgreements.sortNameDesc',
        'client|asc': 'pricingAgreements.sortCustomerAsc',
        'client|desc': 'pricingAgreements.sortCustomerDesc',
        'validFrom|desc': 'pricingAgreements.sortValidFromDesc',
        'validFrom|asc': 'pricingAgreements.sortValidFromAsc',
      };
      return this.sortTypeIds.map((id) => ({ id, name: this.$t(keys[id] || id) }));
    },
    templateDownloadUrl() {
      const base = process.env.API_LOCATION || '';
      return `${base.replace(/\/$/, '')}/docs/pricing-agreement-template.xlsx`;
    },
    hasUnmatched() {
      return this.importResult && this.importResult.unmatched && this.importResult.unmatched.length > 0;
    },
    unmatchedRows() {
      return this.hasUnmatched ? this.importResult.unmatched : [];
    },
    unmatchedPagination() {
      const total = this.unmatchedRows.length;
      const limit = this.unmatchedPerPage;
      return {
        currentPage: this.currentUnmatchedPage,
        lastPage: Math.max(1, Math.ceil(total / limit)),
        limit,
        totalCount: total,
      };
    },
    paginatedUnmatchedRows() {
      if (!this.hasUnmatched) return [];
      const start = (this.currentUnmatchedPage - 1) * this.unmatchedPerPage;
      return this.unmatchedRows.slice(start, start + this.unmatchedPerPage);
    },
  },

  watch: {
    '$route.query.import'(val) {
      this.showImportDialog = val === '1';
    },
    '$route.query.filter'(val) {
      this.activeFilter = (val === 'active' || val === 'inactive' || val === 'all') ? val : 'inactive';
    },
    searchQuery() {
      this.currentPage = 1;
    },
    showImportDialog(val) {
      if (!val) {
        this.importResult = null;
        this.unmatchedSelections = {};
      }
    },
    view(view) {
      if (view === 'card') {
        this.info = 'info';
        this.primary = 'primary';
      } else {
        this.primary = 'info';
        this.info = 'primary';
      }
    },
  },

  mounted() {
    this.setInnerSidebar([
      {
        title: this.$t('layouts.sidebar.clients'),
        options: [
          {
            title: this.$t('layouts.sidebar.active'),
            route: {
              name: 'clients.index',
              query: { filter: 'active' },
            },
          },
          {
            title: this.$t('layouts.sidebar.inActive'),
            route: {
              name: 'clients.index',
              query: { filter: 'inActive' },
            },
          },
          {
            title: this.$t('layouts.sidebar.orderedPastWeek'),
            route: {
              name: 'clients.index',
              query: { filter: 'orderedPastWeek' },
            },
          },
          {
            title: this.$t('layouts.sidebar.orderedPastMonth'),
            route: {
              name: 'clients.index',
              query: { filter: 'orderedPastMonth' },
            },
          },
          {
            title: this.$t('layouts.sidebar.neverOrdered'),
            route: {
              name: 'clients.index',
              query: { filter: 'neverOrdered' },
            },
          },
          {
            title: this.$t('layouts.sidebar.storeSynced'),
            route: {
              name: 'clients.index',
              query: { filter: 'storeSynced' },
            },
          },
          {
            title: this.$t('layouts.sidebar.all'),
            route: { name: 'clients.index' },
          },
        ],
        button: {
          title: this.$t('button.addClient'),
          route: { name: 'clients.create' },
        },
      },
      {
        title: this.$t('layouts.sidebar.clientGroup'),
        options: [
          {
            title: this.$t('layouts.sidebar.active'),
            route: {
              name: 'clientGroups.index',
              query: { filter: 'active' },
            },
          },
          {
            title: this.$t('layouts.sidebar.inActive'),
            route: {
              name: 'clientGroups.index',
              query: { filter: 'inActive' },
            },
          },
          {
            title: this.$t('layouts.sidebar.newOrders'),
            route: {
              name: 'clientGroups.index',
              query: { filter: 'new' },
            },
          },
          {
            title: this.$t('layouts.sidebar.all'),
            route: { name: 'clientGroups.index' },
          },
        ],
        button: {
          title: this.$t('button.addClientGroup'),
          route: { name: 'clientGroups.create' },
        },
      },
      {
        title: this.$t('layouts.sidebar.clientAgreements'),
        options: [
          {
            title: this.$t('layouts.sidebar.active'),
            route: {
              name: 'pricingAgreements.index',
              query: { filter: 'active' },
            },
          },
          {
            title: this.$t('layouts.sidebar.inActive'),
            route: {
              name: 'pricingAgreements.index',
              query: { filter: 'inactive' },
            },
          },
          {
            title: this.$t('layouts.sidebar.all'),
            route: {
              name: 'pricingAgreements.index',
              query: { filter: 'all' },
            },
          },
          {
            title: this.$t('layouts.sidebar.importAgreement'),
            route: {
              name: 'pricingAgreements.index',
              query: { import: '1' },
            },
          },
        ],
        button: {
          title: this.$t('layouts.sidebar.newAgreement'),
          route: { name: 'pricingAgreements.create' },
        },
      },
    ]);
    const filter = this.$route.query.filter;
    this.activeFilter = (filter === 'active' || filter === 'inactive' || filter === 'all') ? filter : 'inactive';
    if (!this.$route.query.filter && this.$route.name === 'pricingAgreements.index') {
      this.$router.replace({ name: 'pricingAgreements.index', query: { ...this.$route.query, filter: 'inactive' } }).catch(() => {});
    }
    if (this.$route.query.import === '1') {
      this.showImportDialog = true;
    }
    if (this.view === 'card') {
      this.info = 'info';
      this.primary = 'primary';
    } else {
      this.primary = 'info';
      this.info = 'primary';
    }
    this.fetch();
    Vue.$http.get('clients?basic_fields=1').then(({ data }) => {
      this.clients = data.data || [];
    }).catch(() => {});
  },

  beforeDestroy() {
    this.unsetInnerSidebar();
  },

  methods: {
    ...mapMutations({
      setInnerSidebar: 'SET_INNERSIDEBAR',
      unsetInnerSidebar: 'UNSET_INNERSIDEBAR',
    }),

    fetch() {
      this.loading = true;
      const clientId = this.$route.query.client_id;
      const url = clientId ? `pricing_agreements?client_id=${clientId}` : 'pricing_agreements';
      Vue.$http.get(url)
        .then(({ data }) => {
          const raw = data && data.data !== undefined ? data.data : data;
          this.items = Array.isArray(raw) ? raw : (Array.isArray(raw && raw.data) ? raw.data : []);
          this.currentPage = 1;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    onPageChange(page, limit) {
      this.currentPage = page;
      this.perPage = limit;
    },

    changeView(view) {
      if (this.info !== view) {
        this.primary = this.info;
        this.info = view;
        if (this.info === 'info') {
          store.dispatch('setView', 'card');
        } else {
          store.dispatch('setView', 'list');
        }
      }
    },

    onChangeSearch(name, value) {
      this.searchQuery = value;
      this.currentPage = 1;
    },

    onChangeSort(name, value) {
      this.sort = value;
      this.currentPage = 1;
    },

    onUnmatchedPageChange(page) {
      this.currentUnmatchedPage = page;
    },

    goToEdit(agreement) {
      this.$router.push({ name: 'pricingAgreements.edit', params: { id: agreement.id } });
    },

    editInNewTab(agreement) {
      const route = this.$router.resolve({ name: 'pricingAgreements.edit', params: { id: agreement.id } });
      window.open(route.href, '_blank');
    },

    deleteAgreement(agreement) {
      this.$confirm(
        this.$t('pricingAgreements.deleteAgreementConfirm'),
        this.$t('pricingAgreements.confirm'),
        { type: 'warning' }
      ).then(() => {
        this.deletingId = agreement.id;
        Vue.$http.delete(`pricing_agreements/${agreement.id}`)
          .then(() => {
            this.$message({ message: this.$t('pricingAgreements.agreementDeleted'), type: 'success' });
            this.deletingId = null;
            this.fetch();
          })
          .catch(() => {
            this.deletingId = null;
            this.$message({ message: this.$t('pricingAgreements.errorDeletingAgreement'), type: 'error' });
          });
      }).catch(() => {});
    },

    onImportFileChange(file) {
      this.importFile = file.raw;
    },
    onImportFileRemove() {
      this.importFile = null;
    },

    ensureUnmatchedSelection(row) {
      const key = row.row_index;
      if (!this.unmatchedSelections[key]) {
        this.$set(this.unmatchedSelections, key, {
          productId: row.product_id || null,
          productOptions: [],
          loading: false,
          discountPercent: row.discount_percent != null ? row.discount_percent : null,
          discountAmount: row.discount_amount != null ? row.discount_amount : null,
        });
      }
      return this.unmatchedSelections[key];
    },

    remoteProductSearchForUnmatched(query, row) {
      const sel = this.ensureUnmatchedSelection(row);
      const searchTerm = (typeof query === 'string' && query.trim()) ? query.trim() : (row.product_name || '').trim();
      if (!searchTerm) {
        sel.loading = false;
        sel.productOptions = [];
        return;
      }
      sel.loading = true;
      productSearchService({ search: searchTerm, filter: 'active' })
        .then((products) => {
          const list = products || [];
          const term = searchTerm.trim().toLowerCase();
          sel.productOptions = list.filter((p) => {
            const name = p.name ? String(p.name).trim().toLowerCase() : '';
            return name && name.indexOf(term) !== -1;
          });
          sel.loading = false;
        })
        .catch(() => { sel.loading = false; });
    },

    addUnmatchedLine(row) {
      const key = row.row_index;
      const sel = this.unmatchedSelections[key];
      const productId = (sel && sel.productId) || row.product_id;
      const discountPercent = (sel && (sel.discountPercent !== undefined && sel.discountPercent !== null && sel.discountPercent !== '')) ? Number(sel.discountPercent) : (row.discount_percent != null ? row.discount_percent : null);
      const discountAmount = (sel && (sel.discountAmount !== undefined && sel.discountAmount !== null && sel.discountAmount !== '')) ? Number(sel.discountAmount) : (row.discount_amount != null ? row.discount_amount : null);
      if (!sel || !productId) {
        this.$message({ message: this.$t('pricingAgreements.productAndMinQtyRequired'), type: 'warning' });
        return;
      }
      if (discountPercent == null && discountAmount == null) {
        this.$message({ message: this.$t('pricingAgreements.setDiscountOrAmount'), type: 'warning' });
        return;
      }
      const agreementId = this.importResult.agreement && this.importResult.agreement.id;
      if (!agreementId) return;
      const payload = {
        product_id: productId,
        min_qty_or_pack_qty: row.min_qty,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        comment: row.comment,
      };
      Vue.$http.post(`pricing_agreements/${agreementId}/lines`, payload)
        .then(() => {
          this.$message({ message: this.$t('pricingAgreements.lineAdded'), type: 'success' });
          const idx = this.importResult.unmatched.findIndex((r) => r.row_index === key);
          if (idx !== -1) this.importResult.unmatched.splice(idx, 1);
          this.$delete(this.unmatchedSelections, key);
          const total = this.importResult.unmatched.length;
          const maxPage = Math.max(1, Math.ceil(total / this.unmatchedPerPage));
          if (this.currentUnmatchedPage > maxPage) {
            this.currentUnmatchedPage = maxPage;
          }
          if (total === 0) {
            this.closeUnmatchedAndGoToEdit();
          }
        })
        .catch(() => {
          this.$message({ message: this.$t('pricingAgreements.errorAddingProduct'), type: 'error' });
        });
    },

    closeUnmatchedAndGoToEdit() {
      const id = this.importResult && this.importResult.agreement && this.importResult.agreement.id;
      this.showImportDialog = false;
      this.importResult = null;
      this.unmatchedSelections = {};
      if (id) {
        this.$router.push({ name: 'pricingAgreements.edit', params: { id } });
      }
    },

    doImport() {
      if (!this.importForm.client_id) {
        this.$message({ message: 'Select a customer', type: 'warning' });
        return;
      }
      if (!this.importFile) {
        this.$message({ message: 'Choose a CSV or XLSX file', type: 'warning' });
        return;
      }
      const clientId = Number(this.importForm.client_id);
      const customerHasAgreement = this.items.some(
        (a) => (a.client_id || (a.client && a.client.id)) === clientId
      );
      if (customerHasAgreement) {
        this.$confirm(
          this.$t('pricingAgreements.customerHasAgreementConfirm'),
          this.$t('pricingAgreements.confirm'),
          { type: 'warning' }
        ).then(() => this.submitImport()).catch(() => {});
        return;
      }
      this.submitImport();
    },

    submitImport() {
      const formData = new FormData();
      formData.append('client_id', this.importForm.client_id);
      if (this.importForm.name) formData.append('name', this.importForm.name);
      formData.append('file', this.importFile);
      this.importing = true;
      Vue.$http.post('pricing_agreements/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(({ data }) => {
          this.importing = false;
          const d = data.data || {};
          const linesCreated = d.lines_created || 0;
          const linesSkipped = (d.lines_skipped || []);
          const skipCount = (d.lines_skipped || []).length;
          const unmatched = d.unmatched || [];
          let msg = this.$t('pricingAgreements.importCreated', { count: linesCreated });
          if (skipCount > 0) {
            msg += ' ' + this.$t('pricingAgreements.importSkipped', { count: skipCount });
          }
          if (unmatched.length > 0) {
            msg += ' ' + this.$t('pricingAgreements.needToMatch', { count: unmatched.length });
          }
          this.$message({
            message: msg,
            type: 'success',
            duration: (skipCount > 0 || unmatched.length > 0) ? 8000 : 3000,
          });
          this.fetch();
          if (unmatched.length > 0 && d.agreement && d.agreement.id) {
            this.importResult = d;
            this.unmatchedSelections = {};
            this.currentUnmatchedPage = 1;
            unmatched.forEach((row) => {
              this.$set(this.unmatchedSelections, row.row_index, {
                productId: row.product_id || null,
                productOptions: [],
                loading: false,
                discountPercent: row.discount_percent != null ? row.discount_percent : null,
                discountAmount: row.discount_amount != null ? row.discount_amount : null,
              });
            });
            this.importForm = { client_id: null, name: '' };
            this.importFile = null;
            if (this.$refs.importUpload) this.$refs.importUpload.clearFiles();
            return;
          }
          this.showImportDialog = false;
          this.importResult = null;
          this.unmatchedSelections = {};
          this.importForm = { client_id: null, name: '' };
          this.importFile = null;
          if (this.$refs.importUpload) this.$refs.importUpload.clearFiles();
          if (d.agreement && d.agreement.id) {
            this.$router.push({ name: 'pricingAgreements.edit', params: { id: d.agreement.id } });
          }
        })
        .catch((err) => {
          this.importing = false;
          const msg = (err.response && err.response.data && err.response.data.error && err.response.data.error.message) ||
            (err.response && err.response.data && err.response.data.message) ||
            this.$t('pricingAgreements.importFailed');
          this.$message({ message: msg, type: 'error' });
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/pricingAgreements/index/index.js